
import React, { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Building2, Handshake } from "lucide-react";
import { usePartners } from "@/hooks/usePartners";
import { hasValidSupabaseConfig } from "@/utils/supabaseDebug";

const Partners = () => {
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();
  const isInitialized = hasValidSupabaseConfig();
  const { partners, isLoading, loadPartners } = usePartners(isInitialized);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.13 }
    );
    const section = document.getElementById("partners");
    if (section) observer.observe(section);
    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  // Load partners data when component mounts
  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  return (
    <section
      id="partners"
      className="py-12 sm:py-20 bg-white border-t border-gray-100"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <div className="flex justify-center items-center gap-2 mb-2">
            <Handshake className="w-7 h-7 text-storystore-red" />
            <span className="text-lg font-semibold text-storystore-red">Våra partner</span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}>
            De <span className="text-storystore-red">företag</span> vi samarbetar med
          </h2>
          <p className={`text-lg text-gray-600 transition-all duration-700 delay-100 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}>
            Tillsammans levererar vi värde. Här presenterar vi några av våra samarbetspartners.
          </p>
        </div>
        
        {isLoading ? (
          <div className="text-center">
            <p>Laddar partners...</p>
          </div>
        ) : (
          <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}>
            {partners.map((partner, idx) => (
              <div
                key={partner.id}
                className={`group flex flex-col items-center justify-center bg-white shadow-md rounded-xl py-6 px-4 hover:shadow-lg transition-shadow duration-300 cursor-pointer border border-gray-50
                  transition-all delay-${idx * 60}`}
              >
                <div className="w-24 h-24 mb-3 flex items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 relative">
                  <img
                    src={partner.logo_url || '/placeholder.svg'}
                    alt={partner.name}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute bottom-2 right-2 bg-white p-1 rounded-full shadow">
                    <Building2 className="w-4 h-4 text-storystore-red" />
                  </span>
                </div>
                <div className="font-semibold text-gray-700 text-center">{partner.name}</div>
              </div>
            ))}
          </div>
        )}
        
        {!isLoading && partners.length === 0 && (
          <div className="text-center text-gray-500">
            <p>Inga partners hittades. Lägg till några i admin-panelen.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Partners;
